import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearSelectedCertificate, resetCertificateData } from '../features/employer-certificate/employer-certificate.slice';
import { EmployerCertificateList } from './employer-certificate/employer-certificate-list.component';
import { EmployerCertificateOrderForm } from './employer-certificate/employer-certificate-order-form.component';
import './employer-certificate-content.component.scss';

export const EmployerCertificateContent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<any>();
    const [searchParams] = useSearchParams();
    const certificateId = searchParams.get('certificateId');
    const isEmployerCertificateDraft = searchParams.get('isEmployerCertificateDraft') === 'true';
    /* URL is the single source of truth for whether the form is
       showing. Previously a local `showOrderForm` React state drove
       the new-order view — but that means clicking Order Certificate
       triggered a re-render WITHOUT pushing a browser history entry,
       so browser back skipped the list and returned to whichever tab
       was open BEFORE the employer-certificate page. Reading
       `?form=new` from the URL makes back / forward navigation
       behave naturally: pushing the flag opens the form, dropping
       the flag (via back or Cancel) restores the list. Edit mode
       (already driven by `?certificateId=X`) continues to work as
       before. */
    const formParam = searchParams.get('form');
    const showForm = formParam === 'new' || !!certificateId;

    const handleOrderCertificate = () => {
        dispatch(clearSelectedCertificate());
        dispatch(resetCertificateData());
        /* Push a new URL entry with `form=new` so:
             1. Browser back navigates back to the LIST view (the
                original `?lang=en-us` URL is one entry down in the
                history stack), not to the previous app tab.
             2. A hard-refresh on the form URL still lands on the
                form — the view is deterministic from the URL alone. */
        const params = new URLSearchParams(location.search);
        params.set('form', 'new');
        navigate(`${location.pathname}?${params.toString()}`);
    };

    const handleBackToList = () => {
        /* Cancel / after-submit-success — pop the pushed history entry
           so the URL naturally reverts to whatever was there before
           the form was opened (list view with the original query
           string intact). This mirrors the browser back button so
           back and Cancel behave the same way for the user.

           Defensive fallback: if for any reason there's no prior entry
           to pop (e.g. deep-link straight into the form), do a plain
           URL cleanup instead so the user at least ends up on the
           list view of this page rather than exiting the app. */
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        const params = new URLSearchParams(location.search);
        params.delete('certificateId');
        params.delete('isEmployerCertificateDraft');
        params.delete('form');
        const newSearch = params.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    };

    return (
        <div className="employer-certificate-content">
            {!showForm ? (
                <EmployerCertificateList onOrderCertificate={handleOrderCertificate} />
            ) : (
                <EmployerCertificateOrderForm
                    onCancel={handleBackToList}
                    onSubmitSuccess={handleBackToList}
                    editCertificateId={certificateId ? Number(certificateId) : undefined}
                    isEmployerCertificateDraft={isEmployerCertificateDraft}
                />
            )}
        </div>
    );
};
